import { DatePickerView } from '@mui/lab/DatePicker/shared';
import { Checkbox, FormControlLabel, Grid, useEventCallback } from '@mui/material';
import { DateTime } from 'luxon';
import { memo } from 'react';
import { PmiButton } from 'shell/components/shared/button';
import ChooseDate from 'shell/components/shared/choice-date';
import ListGender from 'shell/components/shared/gender';
import PmiTextField from 'shell/components/shared/text-field';
import { ParenthoodSearchForm } from 'subscription/view-models/common-interface-subscription';

interface FormFilterComponentProps {
  onChange: (
    key: keyof ParenthoodSearchForm,
    value: ParenthoodSearchForm[keyof ParenthoodSearchForm]
  ) => void;
  values?: ParenthoodSearchForm;
  onSearch: () => void;
  disabled?: boolean;
  onChangeFields: (values: ParenthoodSearchForm | null) => void;
  resetForm: () => void;
}

const FormFilterComponent = ({
  onChange,
  values,
  onSearch,
  disabled,
  onChangeFields,
}: FormFilterComponentProps): React.ReactElement => {
  const resetForm = useEventCallback(() => {
    onChangeFields(null);
  });

  const handleBirthDate = useEventCallback((value?: DateTime | null, choice?: DatePickerView[]) => {
    onChangeFields({
      ...(values || {}),
      birthDate: value,
      birthChoiceDate: choice,
    });
  });

  return (
    <Grid item xs={12} container spacing={1}>
      <Grid item xs={12} md={6}>
        <PmiTextField
          label="Codice fiscale"
          aria-label="Codice fiscale"
          name="fiscalCodeSearchPerson"
          id="fiscalCodeSearchPerson"
          fullWidth
          value={values?.fiscalCodeSearchPerson?.toUpperCase() || ''}
          onChange={(event) => onChange('fiscalCodeSearchPerson', event.target.value)}
          disabled={disabled}
          inputProps={{
            maxLength: '16',
          }}
        />
      </Grid>
      <Grid
        item
        container
        spacing={0}
        rowSpacing={{ xs: 1, md: 0 }}
        columnSpacing={{ xs: 0, md: 1 }}
        xs={12}
        alignItems="center"
      >
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Cognome"
            aria-label="Cognome"
            id="surnameSearchPerson"
            fullWidth
            value={values?.surnameSearchPerson || ''}
            onChange={(event) => onChange('surnameSearchPerson', event.target.value)}
            disabled={disabled || values?.withoutSurnameSearchPerson}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutSurnameSearchPerson"
                checked={values?.withoutSurnameSearchPerson}
                onChange={() =>
                  onChangeFields({
                    surname: !values?.withoutSurnameSearchPerson ? '' : values?.surname || '',
                    withoutSurnameSearchPerson: !values?.withoutSurnameSearchPerson,
                  })
                }
                disabled={disabled}
                inputProps={{ 'aria-label': 'Persona senza cognome' }}
              />
            }
            label="Persona senza cognome"
          />
        </Grid>
      </Grid>
      <Grid
        item
        container
        spacing={0}
        rowSpacing={{ xs: 1, md: 0 }}
        columnSpacing={{ xs: 0, md: 1 }}
        xs={12}
        alignItems="center"
      >
        <Grid item xs={12} md={9}>
          <PmiTextField
            label="Nome"
            aria-label="Nome"
            id="nameSearchPerson"
            fullWidth
            value={values?.nameSearchPerson || ''}
            onChange={(event) => onChange('nameSearchPerson', event.target.value)}
            disabled={disabled || values?.withoutNameSearchPerson}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Checkbox
                id="withoutNameSearchPerson"
                checked={values?.withoutNameSearchPerson}
                onChange={() =>
                  onChangeFields({
                    name: !values?.withoutNameSearchPerson ? '' : values?.name || '',
                    withoutNameSearchPerson: !values?.withoutNameSearchPerson,
                  })
                }
                disabled={disabled}
                inputProps={{ 'aria-label': 'Persona senza nome' }}
              />
            }
            label="Persona senza nome"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <ChooseDate
          selected={values?.birthDate}
          choice={values?.birthChoiceDate}
          onChange={handleBirthDate}
          disabled={disabled}
        />
      </Grid>
      <Grid
        item
        container
        spacing={0}
        rowSpacing={{ xs: 1, md: 0 }}
        columnSpacing={{ xs: 0, md: 1 }}
        alignItems="center"
        xs={12}
      >
        <Grid item xs={12} md={6}>
          <ListGender
            value={values?.gender || ''}
            onChange={(event) => onChange('gender', event.target.value)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PmiButton
            variant="contained"
            color="secondary"
            fullWidth
            disabled={disabled}
            onClick={onSearch}
          >
            Cerca
          </PmiButton>
        </Grid>
        <Grid item xs={12} md={2}>
          <PmiButton variant="outlined" color="cdmBrand" fullWidth onClick={resetForm}>
            Pulisci
          </PmiButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

FormFilterComponent.displayName = 'Form Filter';

const FormFilter = memo(FormFilterComponent);

export default FormFilter;
